import { t } from "@lingui/macro";
import { useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import type { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout/icons/HoleCountIcon";
import type { TransportationCode } from "@whoosh/design-system/src/components/icons_v2/transportation/icons/TeeSheetTransportationIcon";

import { EventCardPairingsQuery } from "../EventCardPairings";
import type { EventCardPairingsQuery as EventCardPairingsQueryType } from "../__generated__/EventCardPairingsQuery.graphql";

export type EventCardPairingRound = {
  name: string;
  courseLayout: HoleCount;
  transportationType: TransportationCode;
  isYou: boolean;
};

export type EventCardPairingBookingItem = {
  time: string | undefined;
  courseName: string | undefined;
  isMultiCourse: boolean;
  rounds: EventCardPairingRound[];
};

export function useGetEventCardPairings(
  queryRef: PreloadedQuery<EventCardPairingsQueryType>
): EventCardPairingBookingItem[] {
  const data = usePreloadedQuery<EventCardPairingsQueryType>(
    EventCardPairingsQuery,
    queryRef
  );
  const node = data.node;
  const currentUserId = data.session?.currentClientProfile?.member?.id ?? null;

  return useMemo(() => {
    if (!node || node.__typename !== "EventSession") return [];
    const bookingEdges = node.bookings?.edges ?? [];
    const isMultiCourse = !!node.courses?.count && node.courses.count > 1;
    const bookings: EventCardPairingBookingItem[] = [];

    for (const bookingEdge of bookingEdges) {
      const booking = bookingEdge?.node;
      if (booking && booking.__typename === "StandardBooking") {
        const startSlot = booking.startSlot;
        bookings.push({
          time: startSlot?.time,
          courseName: startSlot?.course?.name ?? undefined,
          isMultiCourse,
          rounds:
            booking.rounds?.edges?.map((e) => {
              const roundNode = e?.node;
              const hasPlayerName = roundNode?.player?.firstName;
              const name = hasPlayerName
                ? `${roundNode?.player?.firstName ?? ""} ${
                    roundNode?.player?.lastName ?? ""
                  }`.trim()
                : roundNode?.__typename === "MemberRound"
                ? t`Member Hold`
                : roundNode?.__typename === "GuestRound"
                ? t`Unknown Guest`
                : t`Unknown Customer`;
              return {
                name,
                courseLayout:
                  (roundNode?.courseLayout?.holeCount as
                    | HoleCount
                    | undefined) ?? "NONE",
                transportationType:
                  (roundNode?.transportationType?.code as
                    | TransportationCode
                    | undefined) ?? "WALK",
                isYou:
                  !!currentUserId && roundNode?.player?.id === currentUserId,
              };
            }) ?? [],
        });
      }
    }
    return bookings.sort((a, b) => {
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      return 0;
    });
  }, [currentUserId, node]);
}
